import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import Drawer from "@mui/joy/Drawer";
import ModalClose from "@mui/joy/ModalClose";
import { Box, Divider, Step, StepIndicator, Stepper, Typography } from "@mui/joy";
import AddressPage from "./pages/AddressPage";
import StancePage from "./pages/StancePage";
import ContactPage from "./pages/ContactPage";

function ContactModal({ bill, open, setOpen }) {
  const [representative, setRepresentative] = useState({});
  const [address, setAddress] = useState("");
  const [stance, setStance] = useState("");
  const [page, setPage] = useState("address");
  const previouslyFocusedElement = useRef(null);

  async function findRepresentative(address) {
    if (address.length > 0) {
      const rep = await fetch(`/api/member/address/${address}`).then((r) => r.json());
      setRepresentative(rep);
    }
  }

  useEffect(() => {
    findRepresentative(address);
  }, [address]);

  useEffect(() => {
    if (open) {
      previouslyFocusedElement.current = document.activeElement;
    } else {
      if (previouslyFocusedElement.current) {
        previouslyFocusedElement.current.focus();
      }
      // Reset after close animation
      const t = setTimeout(() => {
        setPage("address");
        setRepresentative({});
        setStance("");
        setAddress("");
      }, 300);
      return () => clearTimeout(t);
    }
  }, [open]);

  const handleClose = () => setOpen(false);

  const stepIndex = page === "address" ? 0 : page === "stance" ? 1 : 2;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      slotProps={{
        content: {
          sx: {
            width: { xs: "100%", sm: "480px" },
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          },
        },
        backdrop: {
          sx: { backdropFilter: "blur(1px)" },
        },
      }}
    >
      {/* Fixed header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: "24px",
          py: "18px",
          flexShrink: 0,
        }}
      >
        <Typography sx={{ fontSize: "18px", fontWeight: 700, color: "#171a1c" }}>
          Contact Your Representative
        </Typography>
        <ModalClose
          variant="plain"
          sx={{ position: "relative", top: "auto", right: "auto", "--IconButton-size": "32px" }}
          aria-label="Close"
        />
      </Box>

      <Divider />

      {/* Stepper */}
      <Box sx={{ px: "24px", py: "16px", flexShrink: 0 }}>
        <Stepper sx={{ width: "100%" }} aria-label="Contact form progress">
          <Step
            orientation="vertical"
            sx={{ textAlign: "center" }}
            indicator={
              <StepIndicator variant="solid" color="primary" sx={{ bgcolor: "#003049" }}>
                1
              </StepIndicator>
            }
          >
            Your address
          </Step>
          <Step
            orientation="vertical"
            sx={{ textAlign: "center" }}
            indicator={
              <StepIndicator
                variant={stepIndex >= 1 ? "solid" : "outlined"}
                color="primary"
                sx={stepIndex >= 1 ? { bgcolor: "#003049" } : {}}
              >
                2
              </StepIndicator>
            }
          >
            Your position
          </Step>
          <Step
            orientation="vertical"
            sx={{ textAlign: "center" }}
            indicator={
              <StepIndicator
                variant={stepIndex >= 2 ? "solid" : "outlined"}
                color="primary"
                sx={stepIndex >= 2 ? { bgcolor: "#003049" } : {}}
              >
                3
              </StepIndicator>
            }
          >
            Your message
          </Step>
        </Stepper>
      </Box>

      <Divider />

      {/* Scrollable content */}
      <Box
        sx={{ flex: 1, overflowY: "auto", px: "24px", py: "28px" }}
        id="contact-drawer-content"
        aria-label="Contact form"
      >
        {page === "address" ? (
          <AddressPage setOpen={setOpen} setPage={setPage} setAddress={setAddress} />
        ) : page === "stance" ? (
          <StancePage setPage={setPage} setStance={setStance} bill={bill} />
        ) : (
          <ContactPage
            setOpen={setOpen}
            representative={representative}
            stance={stance}
            bill={bill}
            address={address}
          />
        )}
      </Box>
    </Drawer>
  );
}

ContactModal.propTypes = {
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired,
  bill: PropTypes.object.isRequired,
};

export default ContactModal;
